import { ReactNode, useEffect, useRef } from 'react';
import Router from 'next/router';
import { Field, useFormState, FormRenderProps } from 'react-final-form';
import styled, { StyledComponentProps } from 'styled-components';
import { useNotification } from '../../lib/notification';
import { useTranslation } from '../../lib/hooks';
import { ThemeProps } from '../../lib/theme';
import Button, { ButtonProps } from '../Button';
import Heading from '../Heading';
import Loader, { CircleLoader } from '../Loader';
import { SmallText } from '../Typography';
import { fieldStyle } from './Field';

export const ErrorMsg = styled.p`
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.status.error};
`;

interface FormBlockProps {
  isLoading?: boolean;
  preventDirtyNavigation?: boolean;
}

export const FieldRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 90px;

  > * {
    flex: 1 1 47.5%;
    max-width: 47.5%;
  }
`;

export const Fields = styled.div(
  ({ seamless = false }: { seamless?: boolean }) => `
  &:after {
    content: '';
    clear: both;
    display: table;
  }
  > * {
    float: left;
  }
  ${
    seamless
      ? ``
      : `
      margin-right: -0.5em;
      padding-right: 0.5em;

      > * {
        margin-bottom: 0.5em;
        &:not(:last-child){
          margin-right: 0.5em
          ;}
      }
    `
  }

`,
);

export const FormBlockStyle = styled.form<FormBlockProps>`
  text-align: left;

  ${(p) =>
    p.isLoading
      ? `
      pointer-events: none;
      opacity: .65;
      cursor: default;
    `
      : ''}
  .alvar-input {
    ${fieldStyle}
  }
`;

export const FormBlock = ({
  successMsg,
  failureMsg,
  successMsgTimeout = 5000,
  disableNotification = false,
  preventDirtyNavigation = false,
  onValuesChange,
  ...props
}: {
  disableNotification?: boolean;
  successMsg?: string;
  failureMsg?: string;
  successMsgTimeout?: number;
  preventDirtyNavigation?: boolean;
  onValuesChange?: (values: FormRenderProps['values']) => void;
} & StyledComponentProps<'form', ThemeProps, FormBlockProps, never>) => {
  const { values, submitSucceeded, hasSubmitErrors, dirty } = useFormState();
  const isFirstRun = useRef(true);
  const t = useTranslation();
  const unsavedWarning = t('form.warning.unsaved_changes');
  // NOTE: it would be trivial to use this to display error notifications as well :thinking:, not to take that from the form, but as an extra for some cases.
  const { showNotification: showSuccess } = useNotification(
    'form.submitted_notification',
    {
      timeout: successMsgTimeout,
      type: 'success',
      sticky: true,
    },
  );
  const { showNotification: showError } = useNotification(
    'form.submission_failed',
    {
      timeout: successMsgTimeout,
      type: 'error',
      sticky: true,
    },
  );

  useEffect(() => {
    if (!disableNotification) {
      if (submitSucceeded) {
        showSuccess(successMsg);
      }
      if (hasSubmitErrors) {
        showError(failureMsg);
      }
    }
  }, [
    submitSucceeded,
    hasSubmitErrors,
    successMsg,
    failureMsg,
    disableNotification,
    showSuccess,
    showError,
  ]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (onValuesChange) {
      onValuesChange(values);
    }
  }, [values, onValuesChange]);

  useEffect(() => {
    const handleWindowClose = (e: BeforeUnloadEvent) => {
      if (!dirty) {
        return;
      }

      e.preventDefault();
      e.returnValue = unsavedWarning;
    };

    const handleBrowseAway = () => {
      if (!dirty) {
        return;
      }

      if (window.confirm(unsavedWarning)) {
        return;
      }

      Router.events.emit('routeChangeError');

      // Disabling no-throw-literal because throwing an actual error object will cause a runtime error in next/router and not actually block the navigation. Throwing a literal will be treated as an unhandled error and block navigation.
      // eslint-disable-next-line no-throw-literal
      throw 'routeChange aborted. This can be safely ignored.';
    };
    if (preventDirtyNavigation) {
      window.addEventListener('beforeunload', handleWindowClose);
      Router.events.on('routeChangeStart', handleBrowseAway);
    }
    return () => {
      window.removeEventListener('beforeunload', handleWindowClose);
      Router.events.off('routeChangeStart', handleBrowseAway);
    };
  }, [dirty, preventDirtyNavigation, unsavedWarning]);

  return <FormBlockStyle {...props} />;
};

export const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing(1.5)};
  padding-top: ${(p) => p.theme.spacing(3)};

  ${Heading} {
    margin-bottom: 0;
  }
`;

export const FormErrors = styled.p(
  ({ theme: { colors } }) => `
  color: ${colors.status.error};
  font-weight: 500;
`,
);

export const FormFooterActions = styled.div(
  ({ theme: { spacing, colors } }) => `
  padding: ${spacing(2.5)} 0 ${spacing()};
  margin: ${spacing()} 0;
  border-top: 1px solid ${colors.borderColor};
  text-align: center;
`,
);

export const SaveForm = styled(SmallText.withComponent('button')).attrs(
  ({ children, ...props }) => ({
    type: 'submit',
    children: props['aria-busy'] ? (
      <>
        {children || 'Tallenna'}
        <Loader as="span" />
      </>
    ) : (
      children || 'Tallenna'
    ),
    ...props,
  }),
)`
  padding: 1em 1.5em 1em;
  margin: -0.5em -1em -0.5em 0;
  position: relative;

  &[aria-busy='true'] {
    pointer-events: none;
  }

  ${Loader} {
    font-size: 3px; /* Thank mr. Safari */
    margin: auto 0;
    position: absolute;
    left: -0.25em;
    top: 0;
    bottom: 0;
  }

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const textElems = ['p', 'span'];
const defineButtonType = ({
  as: buttonAs,
  typeDefined,
  type,
}: Pick<ButtonProps, 'as' | 'typeDefined' | 'type'>): ButtonProps['type'] =>
  /* If element is displayed as something else than a clickable/focusable thing, it should not have a type in DOM
   * typeDefined tells if the type was originally defined and not just defaulted by Button
   */
  // eslint-disable-next-line no-nested-ternary
  textElems.includes((buttonAs || '') as string)
    ? undefined
    : typeDefined
    ? type
    : 'submit';

const LoadingButton = styled(Button)
  .attrs<ButtonProps>(({ children, ...props }) => ({
    ...props,
    type: defineButtonType(props),
    children: props['aria-busy'] ? (
      <>
        <span className="text-content">{children}</span>
        <CircleLoader />
      </>
    ) : (
      children
    ),
  }))
  .withConfig({
    shouldForwardProp: (prop) => !['typeDefined'].includes(prop),
  })`
  position: relative;

  &[aria-busy='true'] {
    pointer-events: none;
    
    .text-content {
      opacity: 0;
    }
  }

  ${CircleLoader} {
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }
`;

export { LoadingButton as SubmitFormButton };

export const Condition = ({
  when,
  is,
  children,
}: {
  when: string;
  is?: string;
  children: ReactNode;
}) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => (value === is ? children : null)}
  </Field>
);

export { default as EditForm } from '../EditLink';
